import type { Breadcrumb, Event } from '@sentry/nextjs';

/**
 * `AUTH_ACTION_PAGE`, written out: the Sentry setup loads before the app, and `constants/pages`
 * would add its icon imports to that first script. A test keeps the two equal.
 */
export const SENTRY_AUTH_ACTION_PAGE = '/auth/action';

export const isAuthActionPath = (pathname: string) =>
  (pathname.replace(/\/+$/, '') || '/') === SENTRY_AUTH_ACTION_PAGE;

/**
 * Firebase account links carry a one-time code, `oobCode`. A password-reset code lets its reader
 * set the password, so no code goes to Sentry.
 */
// Also the URL-encoded form, `oobCode%3D…`, as when the link sits inside another URL.
const ACTION_CODE = /(oobCode(?:=|%3D))[^&#\s"'\\%]+/gi;
const HAS_ACTION_CODE = /oobCode(?:=|%3D)/i;

export const scrubActionCode = <T>(value: T): T =>
  (typeof value === 'string' ? value.replace(ACTION_CODE, '$1[Filtered]') : value) as T;

/** For `beforeBreadcrumb`: navigation, fetch and console breadcrumbs can hold a URL. */
export const scrubActionCodeFromBreadcrumb = (breadcrumb: Breadcrumb): Breadcrumb => {
  const data = breadcrumb.data
    ? Object.fromEntries(
        Object.entries(breadcrumb.data).map(([key, value]) => [key, scrubActionCode(value)]),
      )
    : breadcrumb.data;
  return { ...breadcrumb, message: scrubActionCode(breadcrumb.message), data };
};

/**
 * For `beforeSend`. A code can reach any string in an event: the page URL Sentry adds, a breadcrumb,
 * console arguments, an exception message, a stack frame's file name. Sentry has already made the
 * event plain data, so every string is cleaned through its JSON.
 */
export const scrubActionCodeFromEvent = (event: Event): Event => {
  try {
    const json = JSON.stringify(event);
    if (!HAS_ACTION_CODE.test(json)) return event;
    return JSON.parse(json.replace(ACTION_CODE, '$1[Filtered]'));
  } catch {
    return {
      ...event,
      ...(event.request && {
        request: {
          ...event.request,
          url: scrubActionCode(event.request.url),
          query_string: scrubActionCode(event.request.query_string),
        },
      }),
      ...(event.breadcrumbs && {
        breadcrumbs: event.breadcrumbs.map(scrubActionCodeFromBreadcrumb),
      }),
    };
  }
};
